// =============================================================================
// io/sync/commissionSchedules.ts — writing one firm's commission rates.
// -----------------------------------------------------------------------------
// One row per (user, firm), composite primary key. The whole `rates` map is
// replaced on every save, which is correct here and would not be elsewhere:
// unlike `user_preferences.prop_templates`, this column is edited by exactly one
// screen that renders every key it holds before it writes.
//
// ── ⚠ WHY A WHOLE-MAP REPLACE IS SAFE HERE AND A PATCH IS NOT ──
//
// The editor is loaded FROM the stored rates, shows every instrument in the map
// as an editable row, and writes back what is on screen. Nothing is hidden, so
// nothing can be reverted unseen — which is exactly the property D61 asks for.
// A patch, by contrast, could never express "remove this instrument", and
// removing a rate is half of what this screen is for.
//
// 🚨 REMOVING A RATE IS A REAL ACTION WITH A REAL COST. An instrument with no
// rate gets NO auto-commission — not a zero fee that someone might notice, but
// silence — so every trade on it starts reading better than it was. The screen
// says so; this module just does not pretend a delete is a no-op.
//
// ── 🚨 THE FIRM KEY IS NORMALIZED, AND MUST MATCH THE READER'S ──
//
// `firm_key` is lowercased so "Apex" and "APEX" cannot become two schedules that
// each charge half the trades. `domain/commissions.normalizeFirm` is the single
// definition; this module calls it rather than lowercasing again, because two
// implementations of one key is how a lookup starts missing.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { normalizeFirm } from '../../domain/commissions';
import { reportFailureAs } from '../diagnostics/errorLog';

export interface ScheduleWriteResult {
  ok: boolean;
  error: string | null;
  /** The normalized key written, so a caller can re-read the right row. */
  firmKey: string | null;
}

/**
 * Store one firm's rates, replacing whatever was there.
 *
 * @param firm  the DISPLAY name, as typed. The key is derived from it.
 * @param rates keyed by ROOT symbol. An empty map is legitimate — it means the
 *              trader removed every instrument, and it must be written rather
 *              than skipped, or the removal silently does not happen.
 */
export async function saveCommissionSchedule(
  sb: SupabaseClient | null,
  userId: string | null,
  firm: string,
  rates: Readonly<Record<string, number>>,
): Promise<ScheduleWriteResult> {
  if (!sb || !userId) {
    return { ok: false, error: 'Not signed in.', firmKey: null };
  }
  const firmKey = normalizeFirm(firm);
  if (!firmKey) {
    // ⚠ A schedule with no firm has nothing to attach to and would be
    // unreachable by the reader, which looks a firm up by name.
    return {
      ok: false,
      error: 'Give the account a firm first — commission rates are stored per firm.',
      firmKey: null,
    };
  }

  const { error } = await sb.from('commission_schedules').upsert({
    user_id: userId,
    firm_key: firmKey,
    firm_display_name: firm.trim(),
    rates,
  }, { onConflict: 'user_id,firm_key' });

  if (error) {
    return {
      ok: false,
      error: reportFailureAs('save commission rates', error, error.message),
      firmKey,
    };
  }
  return { ok: true, error: null, firmKey };
}
